import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarTrigger,
  useSidebar,
} from "@/frontend/components/ui/sidebar";
import { Badge } from "@/frontend/components/ui/badge";
import { AppBranding } from "./AppBranding";
import { NavUser } from "./NavUser";
import { useAlertHistory } from "@/frontend/hooks/useAlertHistory";
import { readCachedNavAccess, useMemberStore } from "@/frontend/stores/memberStore";
import { readCachedCallingAvailable, useCallStore } from "@/frontend/stores/callStore";
import type { Feature } from "@/shared/rbac";
import {
  NavigationRailGroup,
  NavigationRailItem,
  NavigationRailStack,
} from "./NavigationRail";

// Navigation items - Organized by product areas.

// WORKSPACE - Understand what is happening
const workspaceNavigation = [
  { name: "Dashboard", href: "dashboard", feature: "dashboard" },
  { name: "Inbox", href: "inbox", feature: "inbox" },
  { name: "Calls", href: "calls", feature: "calls" },
  { name: "Que", href: "chat", feature: "chat" },
];

// AUTOMATION - Automate Your Workflows
const automationNavigation = [
  { name: "Alerts", href: "alerts", feature: "alerts", showBadge: true },
  { name: "Jobs", href: "jobs", feature: "flows" },
  { name: "Campaigns", href: "campaigns", feature: "campaigns" },
  { name: "AI Agent", href: "ai-agent", feature: "autopilot" },
  { name: "Pulse", href: "pulse", feature: "pulse" },
];

// WORK - Track follow-up work
const workNavigation = [
  { name: "Tasks", href: "tasks", feature: "tasks" },
  { name: "Sales", href: "sales", feature: "sales" },
];

function getCurrentNavigationItem(pathname: string, orgId?: string) {
  if (!orgId) return "";

  const basePath = `/org/${orgId}/app/`;
  if (!pathname.startsWith(basePath)) return "";

  const routeSegment = pathname.substring(basePath.length).split("/")[0];
  const matchingItem = [
    ...workspaceNavigation,
    ...automationNavigation,
    ...workNavigation,
  ].find((item) => item.href === routeSegment);

  if (matchingItem) return matchingItem.name;
  if (routeSegment === "docs") return "User Guide";
  if (routeSegment === "settings") return "Settings";
  return "";
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { orgId } = useParams();
  const [isHoverPreview, setIsHoverPreview] = useState(false);
  const isAdmin = useMemberStore((state) => state.isAdmin);
  const permissions = useMemberStore((state) => state.permissions);
  const fetched = useMemberStore((state) => state.fetched);
  // Until live permissions arrive, draw the menu from this org's last known access so it opens complete.
  const access = fetched ? { isAdmin, permissions } : orgId ? readCachedNavAccess(orgId) : null;
  const showSkeleton = !access;
  const canRead = (feature: Feature) => Boolean(access?.isAdmin || access?.permissions?.[feature]?.read === true);
  const { setOpenMobile, isMobile } = useSidebar();

  // Fetch unread alerts count
  const { data: alertHistory } = useAlertHistory({
    organizationId: canRead("alerts") ? orgId : undefined,
    limit: 1, // We only need the unread count
  });

  const unreadAlertsCount = alertHistory?.unreadCount || 0;

  const current = getCurrentNavigationItem(location.pathname, orgId);
  const liveCallingAvailable = useCallStore((state) => state.callingAvailable);
  const callingAvailable = liveCallingAvailable ?? readCachedCallingAvailable(orgId);
  const missedCallsCount = useCallStore((state) => state.missedCount);
  const visible = <T extends { feature: string }>(items: T[]) =>
    items.filter((item) => canRead(item.feature as Feature) && (item.feature !== "calls" || callingAvailable === true));
  const skeletonRows = (count: number) => Array.from({ length: count }, (_, index) => (
    <SidebarMenuItem key={`skeleton-${index}`}><SidebarMenuSkeleton /></SidebarMenuItem>
  ));

  // Close sidebar on mobile when clicking a navigation item
  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const showExpandedSidebar = !isCollapsed || isHoverPreview;
  const navigationGroupClassName = showExpandedSidebar ? "px-2 py-4" : "hidden";

  return (
    <Sidebar
      collapsible="icon"
      data-hover-preview={isHoverPreview}
      {...props}
      id="app-sidebar"
      className="max-w-[256px]"
      onMouseEnter={() => {
        if (!isMobile && isCollapsed) setIsHoverPreview(true);
      }}
      onMouseLeave={() => setIsHoverPreview(false)}
    >
      <SidebarHeader className="!py-1 group-data-[state=expanded]:w-[256px]">
        {showExpandedSidebar ? (
          <div className="flex items-center justify-between gap-2">
            <AppBranding />
            <SidebarTrigger className="ml-auto hidden md:flex" />
          </div>
        ) : (
          <div className="flex items-center justify-center py-2">
            <SidebarTrigger className="hidden md:flex" />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="gap-0 scrollbar-auto-hide">
        <SidebarGroup className={navigationGroupClassName}>
          <NavigationRailStack>
          <NavigationRailGroup railAsChild>
          <SidebarMenu>
            {showSkeleton ? skeletonRows(workspaceNavigation.length) : visible(workspaceNavigation).map((item) => (
              <SidebarMenuItem key={item.name}>
                <NavigationRailItem asChild active={current === item.name}>
                  <Link
                    to={`/org/${orgId}/app/${item.href}`}
                    onClick={handleNavClick}
                    data-tour={`nav-${item.href}`}
                  >
                    <span>{item.name}</span>
                    {item.feature === "calls" && missedCallsCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="ml-auto h-5 min-w-5 px-1 text-xs"
                        aria-label={`${missedCallsCount} missed ${missedCallsCount === 1 ? "call" : "calls"}`}
                      >
                        {missedCallsCount}
                      </Badge>
                    )}
                  </Link>
                </NavigationRailItem>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
          </NavigationRailGroup>

          <NavigationRailGroup railAsChild>
          <SidebarMenu>
            {showSkeleton ? skeletonRows(automationNavigation.length) : visible(automationNavigation)
              .map((item) => (
              <SidebarMenuItem key={item.name}>
                <NavigationRailItem asChild active={current === item.name}>
                  <Link
                    to={`/org/${orgId}/app/${item.href}`}
                    onClick={handleNavClick}
                    data-tour={`nav-${item.href}`}
                  >
                    <span>{item.name}</span>
                    {item.showBadge && unreadAlertsCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="ml-auto h-5 min-w-5 px-1 text-xs"
                      >
                        {unreadAlertsCount}
                      </Badge>
                    )}
                  </Link>
                </NavigationRailItem>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
          </NavigationRailGroup>

          <NavigationRailGroup railAsChild>
          <SidebarMenu>
            {showSkeleton ? skeletonRows(workNavigation.length) : visible(workNavigation).map((item) => (
              <SidebarMenuItem key={item.name}>
                <NavigationRailItem asChild active={current === item.name}>
                  <Link
                    to={`/org/${orgId}/app/${item.href}`}
                    onClick={handleNavClick}
                    data-tour={`nav-${item.href}`}
                  >
                    <span>{item.name}</span>
                  </Link>
                </NavigationRailItem>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
          </NavigationRailGroup>

          <NavigationRailGroup railAsChild>
          <SidebarMenu>
            <SidebarMenuItem>
              <NavigationRailItem asChild active={current === "Settings"}>
                <Link
                  to={`/org/${orgId}/app/settings`}
                  onClick={handleNavClick}
                  data-tour="nav-settings"
                >
                  <span>Settings</span>
                </Link>
              </NavigationRailItem>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <NavigationRailItem asChild active={current === "User Guide"}>
                <Link
                  to="/docs/getting-started"
                  onClick={handleNavClick}
                >
                  <span>User Guide</span>
                </Link>
              </NavigationRailItem>
            </SidebarMenuItem>
          </SidebarMenu>
          </NavigationRailGroup>
          </NavigationRailStack>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
